import React, { FormEvent, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext, type ThemeName } from "../context/ThemeContext";

type SetupPayload = {
  display_name: string;
  city: string;
  state: string;
  age_range: string;
  theme_color: ThemeName;
  contact_email: string | null;
};

const API_BASE = "https://api.avaaio.com";

const THEMES: Array<{
  name: ThemeName;
  label: string;
  accent: string;
  soft: string;
}> = [
  {
    name: "cyan",
    label: "Neon Cyan",
    accent: "#0895A5",
    soft: "rgba(8,149,165,0.34)",
  },
  {
    name: "purple",
    label: "Purple",
    accent: "#6D3FA8",
    soft: "rgba(109,63,168,0.34)",
  },
  {
    name: "emerald",
    label: "Emerald",
    accent: "#23834A",
    soft: "rgba(35,131,74,0.34)",
  },
  {
    name: "amber",
    label: "Amber",
    accent: "#9A681E",
    soft: "rgba(154,104,30,0.34)",
  },
  {
    name: "lava",
    label: "Lava",
    accent: "#9E3038",
    soft: "rgba(158,48,56,0.34)",
  },
  {
    name: "midnight",
    label: "Midnight",
    accent: "#315D91",
    soft: "rgba(49,93,145,0.34)",
  },
  {
    name: "aurora",
    label: "Aurora",
    accent: "#A83E7D",
    soft: "rgba(168,62,125,0.34)",
  },
  {
    name: "black",
    label: "Black",
    accent: "#303844",
    soft: "rgba(48,56,68,0.34)",
  },
];

function readErrorMessage(data: unknown): string {
  if (!data || typeof data !== "object") {
    return "";
  }

  const record = data as Record<string, unknown>;

  if (typeof record.detail === "string") {
    return record.detail;
  }

  if (typeof record.message === "string") {
    return record.message;
  }

  if (Array.isArray(record.detail)) {
    return record.detail
      .map((item) => {
        if (item && typeof item === "object") {
          const message = (item as Record<string, unknown>).msg;
          return typeof message === "string" ? message : "";
        }
        return "";
      })
      .filter(Boolean)
      .join(" ");
  }

  return "";
}

async function saveProfile(payload: SetupPayload): Promise<void> {
  const memory = {
    display_name: payload.display_name,
    city: payload.city,
    state: payload.state,
    age_range: payload.age_range,
    theme_color: payload.theme_color,
    contact_email: payload.contact_email,
  };

  const attempts = [
    {
      url: `${API_BASE}/api/v1/auth/profile`,
      method: "PATCH",
      body: {
        ...payload,
        memory,
        profile_complete: true,
      },
    },
    {
      url: `${API_BASE}/api/v1/auth/profile`,
      method: "PUT",
      body: {
        ...payload,
        memory,
        profile_complete: true,
      },
    },
    {
      url: "/api/v1/auth/profile",
      method: "PATCH",
      body: {
        ...payload,
        memory,
        profile_complete: true,
      },
    },
  ];

  let lastMessage = "The profile could not be saved.";

  for (const attempt of attempts) {
    try {
      const response = await fetch(attempt.url, {
        method: attempt.method,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(attempt.body),
      });

      const text = await response.text();
      let data: unknown = {};

      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = {};
      }

      if (response.ok) {
        return;
      }

      const serverMessage = readErrorMessage(data);

      if (serverMessage) {
        lastMessage = serverMessage;
      } else {
        lastMessage = `Profile save returned ${response.status}.`;
      }

      /*
       * Try the next compatible endpoint only for routing/method/schema
       * failures. Authentication errors should be shown immediately.
       */
      if (response.status === 401 || response.status === 403) {
        throw new Error(
          "Your secure session expired. Please sign in again and continue setup.",
        );
      }
    } catch (error) {
      if (
        error instanceof Error &&
        error.message.includes("secure session expired")
      ) {
        throw error;
      }

      if (error instanceof Error && error.message) {
        lastMessage = error.message;
      }
    }
  }

  throw new Error(lastMessage);
}

export default function SetupProfile() {
  const navigate = useNavigate();
  const { setTheme } = useContext(ThemeContext);

  const [step, setStep] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [themeColor, setThemeColor] = useState<ThemeName>("cyan");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"error" | "success">("error");
  const [showMemoryConfig, setShowMemoryConfig] = useState(false);
  const [memoryProgress, setMemoryProgress] = useState(0);
  const [memoryStage, setMemoryStage] = useState(
    "Preparing Ava’s secure memory architecture",
  );
  const [memoryConfigurationReady, setMemoryConfigurationReady] =
    useState(false);

  const activeTheme =
    THEMES.find((theme) => theme.name === themeColor) ?? THEMES[0];

  const steps = useMemo(
    () => [
      {
        eyebrow: "Welcome to AVAAIO™",
        title: "What should Ava call you?",
        description:
          "Choose the name Ava will use when speaking and working with you.",
      },
      {
        eyebrow: "Your experience",
        title: "Choose Ava’s color energy",
        description:
          "Your selection updates the orb now and becomes your account theme.",
      },
      {
        eyebrow: "Local personalization",
        title: "Where are you located?",
        description:
          "City and state help Ava personalize supported location-based experiences.",
      },
      {
        eyebrow: "Final details",
        title: "Finish your profile",
        description:
          "These optional details help tailor your AVAAIO experience.",
      },
    ],
    [],
  );

  const current = steps[step];
  const isLastStep = step === steps.length - 1;

  const canContinue =
    step !== 0 || displayName.trim().length > 0;

  function previousStep() {
    setMessage("");
    setStep((currentStep) => Math.max(0, currentStep - 1));
  }

  function nextStep() {
    if (!canContinue) {
      setMessageType("error");
      setMessage("Please enter the name Ava should call you.");
      return;
    }

    setMessage("");
    setStep((currentStep) =>
      Math.min(steps.length - 1, currentStep + 1),
    );
  }

  async function finishSetup(event?: FormEvent) {
    event?.preventDefault();

    if (saving) {
      return;
    }

    if (!displayName.trim()) {
      setStep(0);
      setMessageType("error");
      setMessage("Please enter the name Ava should call you.");
      return;
    }

    setSaving(true);
    setMessage("");

    const payload: SetupPayload = {
      display_name: displayName.trim(),
      city: city.trim(),
      state: stateName.trim(),
      age_range: ageRange,
      theme_color: themeColor,
      contact_email: contactEmail.trim() || null,
    };

    try {
      await saveProfile(payload);

      // __AVAAIO_FIRST_TIME_WELCOME_MEMORY_V3__
      const welcomeStateResponse =
        await fetch(
          `${API_BASE}/api/v1/auth/profile`,
          {
            method: "PATCH",
            credentials: "include",
            headers: {
              "Content-Type":
                "application/json",
              Accept:
                "application/json",
            },
            body: JSON.stringify({
              memory: {
                ava_welcome_completed:
                  false,
                ava_welcome_version:
                  3,
              },
            }),
          },
        );

      const welcomeStateData =
        await welcomeStateResponse
          .json()
          .catch(() => null);

      if (
        !welcomeStateResponse.ok ||
        welcomeStateData?.ok !== true
      ) {
        throw new Error(
          "Ava's first welcome could not be prepared. Please try again.",
        );
      }

      try {
        setTheme(themeColor);
        localStorage.setItem("ava_theme", themeColor);
        localStorage.setItem("ava_theme_color", themeColor);
        localStorage.setItem(
          "ava_setup_profile",
          JSON.stringify({
            ...payload,
            profile_complete: true,
          }),
        );
      } catch {
        // Profile is already securely saved on the server.
      }

      setMessageType("success");
      setMessage("Your AVAAIO profile is ready.");
      setShowMemoryConfig(true);
      setMemoryProgress(8);
      setMemoryStage("Preparing Ava’s secure memory architecture");

      const configurationStages = [
        {
          progress: 22,
          label: `Creating identity profile for ${displayName.trim()}`,
        },
        {
          progress: 38,
          label: "Applying interface and color preferences",
        },
        {
          progress: 54,
          label: "Initializing language and communication settings",
        },
        {
          progress: 70,
          label: "Configuring secure conversation memory",
        },
        {
          progress: 86,
          label: "Synchronizing Ava intelligence services",
        },
        {
          progress: 100,
          label: "Ava memory configuration complete",
        },
      ];

      for (const stage of configurationStages) {
        await new Promise<void>((resolve) => {
          window.setTimeout(resolve, 1250);
        });

        setMemoryProgress(stage.progress);
        setMemoryStage(stage.label);
      }

      try {
        sessionStorage.setItem("ava_setup_completed", "1");
        sessionStorage.removeItem("ava_boot_seen");
        sessionStorage.setItem(
          "ava_first_welcome_name",
          displayName.trim(),
        );
      } catch {}

      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 900);
      });

      setMemoryStage(
        `AVA NEURAL PROFILE ONLINE — Welcome, ${displayName.trim()}`,
      );
      setMemoryConfigurationReady(true);

      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 1400);
      });

      try {
        sessionStorage.removeItem("ava_boot_seen");
        sessionStorage.setItem(
          "ava_first_welcome_name",
          displayName.trim(),
        );
      } catch {}

      navigate("/info?welcome=1", { replace: true });
    } catch (error) {
      console.error("[SetupProfile] save failed:", error);
      setMessageType("error");
      setMessage(
        error instanceof Error && error.message
          ? error.message
          : "The profile could not be saved. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (showMemoryConfig) {
    const neuralSystems = [
      {
        name: "Identity",
        threshold: 22,
        code: "ID",
      },
      {
        name: "Preferences",
        threshold: 38,
        code: "PX",
      },
      {
        name: "Language",
        threshold: 54,
        code: "LN",
      },
      {
        name: "Secure Memory",
        threshold: 70,
        code: "MM",
      },
      {
        name: "Security",
        threshold: 86,
        code: "SC",
      },
      {
        name: "Ava Intelligence",
        threshold: 100,
        code: "AI",
      },
    ];

    return (
      <div
        className="avaaio-neural-v2"
        style={{
          minHeight: "100dvh",
          background:
            "radial-gradient(circle at 50% 40%, rgba(0,220,255,0.12), transparent 30%), linear-gradient(180deg, #01040a 0%, #020812 48%, #010207 100%)",
          color: "#f1fdff",
          fontFamily:
            'Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          overflow: "hidden",
          position: "relative",
        }}
      >
        <div
          aria-hidden="true"
          className="neural-background-grid"
          style={{
            backgroundImage: `
              linear-gradient(${activeTheme.soft} 1px, transparent 1px),
              linear-gradient(90deg, ${activeTheme.soft} 1px, transparent 1px)
            `,
          }}
        />

        <div
          aria-hidden="true"
          className="neural-background-glow neural-glow-left"
          style={{
            background: `radial-gradient(circle, ${activeTheme.soft}, transparent 68%)`,
          }}
        />

        <div
          aria-hidden="true"
          className="neural-background-glow neural-glow-right"
          style={{
            background: `radial-gradient(circle, ${activeTheme.soft}, transparent 68%)`,
          }}
        />

        <main className="neural-v2-shell">
          <header className="neural-v2-header">
            <div
              className="neural-v2-eyebrow"
              style={{
                color: activeTheme.accent,
                textShadow: `0 0 18px ${activeTheme.soft}`,
              }}
            >
              AVAAIO Neural Initialization
            </div>

            <h1>
              {memoryConfigurationReady
                ? "Ava Neural Profile Online"
                : "Configuring Ava Memory"}
            </h1>

            <p>
              {memoryConfigurationReady ? (
                <>
                  Welcome,{" "}
                  <strong style={{ color: activeTheme.accent }}>
                    {displayName.trim()}
                  </strong>
                  . Your personalized Ava intelligence profile is ready.
                </>
              ) : (
                <>
                  Creating a secure personalized intelligence profile for{" "}
                  <strong>{displayName.trim()}</strong>
                </>
              )}
            </p>
          </header>

          <section
            className={`neural-v2-board ${
              memoryConfigurationReady ? "is-ready" : ""
            }`}
            style={{
              borderColor: activeTheme.soft,
              boxShadow: `
                0 28px 100px rgba(0,0,0,0.58),
                0 0 38px ${activeTheme.soft},
                inset 0 0 50px rgba(0,0,0,0.36)
              `,
            }}
          >
            <div
              aria-hidden="true"
              className="neural-board-grid"
              style={{
                backgroundImage: `
                  linear-gradient(${activeTheme.soft} 1px, transparent 1px),
                  linear-gradient(90deg, ${activeTheme.soft} 1px, transparent 1px)
                `,
              }}
            />

            <div
              aria-hidden="true"
              className="neural-data-column neural-data-left"
              style={{ color: activeTheme.accent }}
            >
              <span>01</span>
              <span>10</span>
              <span>11</span>
              <span>01</span>
              <span>10</span>
            </div>

            <div
              aria-hidden="true"
              className="neural-data-column neural-data-right"
              style={{ color: activeTheme.accent }}
            >
              <span>AV</span>
              <span>AI</span>
              <span>IO</span>
              <span>01</span>
              <span>NX</span>
            </div>

            <div className="neural-system-grid">
              {neuralSystems.map((system, index) => {
                const online =
                  memoryConfigurationReady ||
                  memoryProgress >= system.threshold;

                return (
                  <div
                    key={system.name}
                    className={`neural-system-module ${
                      online ? "online" : "initializing"
                    }`}
                    style={{
                      borderColor: online
                        ? activeTheme.accent
                        : "rgba(255,255,255,0.10)",
                      boxShadow: online
                        ? `0 0 18px ${activeTheme.soft}, inset 0 0 15px ${activeTheme.soft}`
                        : "none",
                    }}
                  >
                    <div
                      className="system-module-code"
                      style={{
                        color: online
                          ? activeTheme.accent
                          : "rgba(220,240,245,0.38)",
                      }}
                    >
                      {system.code}
                    </div>

                    <div className="system-module-copy">
                      <strong>{system.name}</strong>
                      <span
                        style={{
                          color: online
                            ? activeTheme.accent
                            : "rgba(210,232,238,0.46)",
                        }}
                      >
                        {online ? "ONLINE" : "INITIALIZING"}
                      </span>
                    </div>

                    <div
                      className="system-module-light"
                      style={{
                        background: online
                          ? activeTheme.accent
                          : "rgba(255,255,255,0.10)",
                        boxShadow: online
                          ? `0 0 12px ${activeTheme.accent}`
                          : "none",
                      }}
                    />

                    <div
                      className={`system-module-scan scan-${index + 1}`}
                      style={{
                        background: `linear-gradient(90deg, transparent, ${activeTheme.accent}, transparent)`,
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <div className="neural-core-zone">
              <div
                className="neural-core-outer-glow"
                style={{
                  background: `radial-gradient(circle, ${activeTheme.soft}, transparent 68%)`,
                }}
              />

              <div
                className="neural-progress-ring"
                style={{
                  background: `conic-gradient(
                    ${activeTheme.accent} ${memoryProgress}%,
                    rgba(255,255,255,0.08) ${memoryProgress}% 100%
                  )`,
                  boxShadow: `
                    0 0 24px ${activeTheme.soft},
                    0 0 62px ${activeTheme.soft}
                  `,
                }}
              >
                <div className="neural-progress-ring-inner">
                  <div
                    className="neural-core-shell"
                    style={{
                      borderColor: activeTheme.accent,
                      boxShadow: `
                        0 0 18px ${activeTheme.accent},
                        0 0 42px ${activeTheme.soft},
                        inset 0 0 26px ${activeTheme.soft}
                      `,
                    }}
                  >
                    <div
                      className="neural-core-sphere"
                      style={{
                        background: `
                          radial-gradient(
                            circle at 34% 27%,
                            #ffffff 0%,
                            #eaffff 8%,
                            ${activeTheme.accent} 24%,
                            ${activeTheme.accent} 43%,
                            #07131f 72%,
                            #010307 100%
                          )
                        `,
                        boxShadow: `
                          0 0 22px ${activeTheme.accent},
                          0 0 66px ${activeTheme.soft},
                          inset 0 0 24px rgba(255,255,255,0.30)
                        `,
                      }}
                    >
                      <div className="neural-core-highlight" />

                      <div
                        className="neural-core-scanline"
                        style={{
                          background: `linear-gradient(
                            90deg,
                            transparent,
                            ${activeTheme.accent},
                            transparent
                          )`,
                        }}
                      />
                    </div>

                    <div
                      className="neural-ring neural-ring-a"
                      style={{ borderColor: activeTheme.accent }}
                    />
                    <div
                      className="neural-ring neural-ring-b"
                      style={{ borderColor: activeTheme.accent }}
                    />
                    <div
                      className="neural-ring neural-ring-c"
                      style={{ borderColor: activeTheme.accent }}
                    />
                  </div>
                </div>
              </div>

              <div
                className="neural-progress-number"
                style={{
                  color: activeTheme.accent,
                  textShadow: `0 0 12px ${activeTheme.soft}`,
                }}
              >
                {memoryProgress}%
              </div>

              <div className="neural-core-label">
                {memoryConfigurationReady
                  ? "AVA CORE ONLINE"
                  : "AVA CORE INITIALIZING"}
              </div>
            </div>

            <div
              aria-hidden="true"
              className="neural-trace trace-a"
              style={{ background: activeTheme.accent }}
            />
            <div
              aria-hidden="true"
              className="neural-trace trace-b"
              style={{ background: activeTheme.accent }}
            />
            <div
              aria-hidden="true"
              className="neural-trace trace-c"
              style={{ background: activeTheme.accent }}
            />
            <div
              aria-hidden="true"
              className="neural-trace trace-d"
              style={{ background: activeTheme.accent }}
            />

            <div
              aria-hidden="true"
              className="neural-pulse pulse-a"
              style={{
                background: activeTheme.accent,
                boxShadow: `0 0 12px ${activeTheme.accent}`,
              }}
            />
            <div
              aria-hidden="true"
              className="neural-pulse pulse-b"
              style={{
                background: activeTheme.accent,
                boxShadow: `0 0 12px ${activeTheme.accent}`,
              }}
            />
            <div
              aria-hidden="true"
              className="neural-pulse pulse-c"
              style={{
                background: activeTheme.accent,
                boxShadow: `0 0 12px ${activeTheme.accent}`,
              }}
            />
          </section>

          <section
            className="neural-v2-status"
            style={{
              borderColor: activeTheme.soft,
              boxShadow: `0 0 24px ${activeTheme.soft}`,
            }}
          >
            <div className="neural-status-row">
              <span
                className="neural-status-dot"
                style={{
                  background: activeTheme.accent,
                  boxShadow: `0 0 13px ${activeTheme.accent}`,
                }}
              />

              <span>{memoryStage}</span>

              <strong style={{ color: activeTheme.accent }}>
                {memoryProgress}%
              </strong>
            </div>

            <div className="neural-status-track">
              <div
                className="neural-status-fill"
                style={{
                  width: `${memoryProgress}%`,
                  background: `linear-gradient(
                    90deg,
                    ${activeTheme.accent},
                    #ffffff
                  )`,
                  boxShadow: `0 0 18px ${activeTheme.soft}`,
                }}
              />
            </div>
          </section>

          <div
            className={`neural-auto-transition ${
              memoryConfigurationReady ? "is-active" : ""
            }`}
            aria-hidden="true"
            style={{
              background: `radial-gradient(circle, ${"${activeTheme.soft}"} 0%, transparent 68%)`,
            }}
          />

          <div
            className="neural-v2-footer"
            style={{
              color: memoryConfigurationReady
                ? activeTheme.accent
                : "rgba(214,239,244,0.46)",
            }}
          >
            {memoryConfigurationReady
              ? "Neural profile synchronized and secured"
              : "Do not close AVAAIO during neural initialization"}
          </div>
        </main>

        <style>
          {`
            .avaaio-neural-v2,
            .avaaio-neural-v2 * {
              box-sizing: border-box;
            }

            .neural-background-grid {
              position: absolute;
              left: -25%;
              right: -25%;
              bottom: -38%;
              height: 95%;
              background-size: 42px 42px;
              opacity: 0.15;
              transform:
                perspective(850px)
                rotateX(64deg);
              transform-origin: center top;
              animation:
                neuralGridTravel
                8s
                linear
                infinite;
            }

            .neural-background-glow {
              position: absolute;
              width: 420px;
              height: 420px;
              border-radius: 50%;
              filter: blur(28px);
              opacity: 0.28;
            }

            .neural-glow-left {
              left: -210px;
              top: 14%;
            }

            .neural-glow-right {
              right: -210px;
              bottom: 4%;
            }

            .neural-v2-shell {
              position: relative;
              z-index: 2;
              width: min(100%, 1120px);
              min-height: 100dvh;
              margin: 0 auto;
              padding:
                max(24px, env(safe-area-inset-top))
                18px
                max(26px, env(safe-area-inset-bottom));
              display: flex;
              flex-direction: column;
              justify-content: center;
            }

            .neural-v2-header {
              text-align: center;
              margin-bottom: 20px;
            }

            .neural-v2-eyebrow {
              font-size: 11px;
              font-weight: 950;
              letter-spacing: 0.29em;
              text-transform: uppercase;
            }

            .neural-v2-header h1 {
              margin: 10px 0 7px;
              font-size: clamp(29px, 5vw, 52px);
              line-height: 1.05;
              letter-spacing: -0.04em;
              text-transform: uppercase;
            }

            .neural-v2-header p {
              margin: 0;
              color: rgba(222,245,250,0.66);
              font-size: clamp(12px, 2vw, 15px);
              letter-spacing: 0.045em;
              line-height: 1.55;
              text-transform: uppercase;
            }

            .neural-v2-board {
              position: relative;
              min-height: 440px;
              border: 1px solid;
              border-radius: 30px;
              overflow: hidden;
              background:
                radial-gradient(
                  circle at center,
                  rgba(20,43,66,0.42),
                  rgba(2,8,16,0.97) 63%
                );
              padding: 24px;
              display: grid;
              place-items: center;
              isolation: isolate;
              transition:
                border-color 300ms ease,
                box-shadow 300ms ease;
            }

            .neural-v2-board.is-ready {
              animation:
                neuralBoardReady
                1.8s
                ease-in-out
                infinite;
            }

            .neural-board-grid {
              position: absolute;
              inset: 0;
              background-size: 36px 36px;
              opacity: 0.08;
              z-index: -1;
            }

            .neural-data-column {
              position: absolute;
              top: 24px;
              bottom: 24px;
              display: flex;
              flex-direction: column;
              justify-content: space-around;
              opacity: 0.24;
              font-size: 10px;
              font-weight: 900;
              letter-spacing: 0.16em;
            }

            .neural-data-left {
              left: 14px;
            }

            .neural-data-right {
              right: 14px;
            }

            .neural-system-grid {
              position: absolute;
              inset: 24px;
              display: grid;
              grid-template-columns:
                minmax(150px, 1fr)
                minmax(230px, 1.35fr)
                minmax(150px, 1fr);
              grid-template-rows:
                repeat(2, minmax(110px, 1fr));
              gap: 18px;
              pointer-events: none;
            }

            .neural-system-module {
              position: relative;
              border: 1px solid;
              border-radius: 19px;
              background:
                linear-gradient(
                  145deg,
                  rgba(12,26,42,0.80),
                  rgba(2,8,16,0.90)
                );
              padding: 15px;
              display: flex;
              align-items: center;
              gap: 12px;
              overflow: hidden;
              transition:
                border-color 350ms ease,
                box-shadow 350ms ease,
                transform 350ms ease;
            }

            .neural-system-module:nth-child(2),
            .neural-system-module:nth-child(5) {
              visibility: hidden;
            }

            .neural-system-module.online {
              transform: translateY(-2px);
            }

            .system-module-code {
              width: 39px;
              height: 39px;
              border: 1px solid currentColor;
              border-radius: 12px;
              display: grid;
              place-items: center;
              font-size: 11px;
              font-weight: 950;
              letter-spacing: 0.08em;
            }

            .system-module-copy {
              display: flex;
              flex-direction: column;
              gap: 4px;
            }

            .system-module-copy strong {
              font-size: 12px;
              letter-spacing: 0.04em;
            }

            .system-module-copy span {
              font-size: 9px;
              font-weight: 950;
              letter-spacing: 0.16em;
            }

            .system-module-light {
              position: absolute;
              top: 13px;
              right: 13px;
              width: 7px;
              height: 7px;
              border-radius: 50%;
            }

            .system-module-scan {
              position: absolute;
              left: -55%;
              top: 0;
              bottom: 0;
              width: 50%;
              opacity: 0;
            }

            .neural-system-module.online
            .system-module-scan {
              animation:
                moduleScan
                2.4s
                ease-in-out
                infinite;
            }

            .neural-core-zone {
              position: relative;
              width: 265px;
              height: 265px;
              display: grid;
              place-items: center;
              z-index: 4;
            }

            .neural-core-outer-glow {
              position: absolute;
              inset: -24%;
              border-radius: 50%;
              filter: blur(20px);
              opacity: 0.74;
              animation:
                neuralOuterGlow
                2.6s
                ease-in-out
                infinite;
            }

            .neural-progress-ring {
              position: relative;
              width: 224px;
              height: 224px;
              border-radius: 50%;
              padding: 8px;
              transition:
                background
                480ms
                ease;
              animation:
                neuralProgressFloat
                4.4s
                ease-in-out
                infinite;
            }

            .neural-progress-ring-inner {
              width: 100%;
              height: 100%;
              border-radius: 50%;
              background:
                radial-gradient(
                  circle,
                  rgba(6,16,28,0.98),
                  rgba(1,5,10,1)
                );
              display: grid;
              place-items: center;
            }

            .neural-core-shell {
              position: relative;
              width: 142px;
              height: 142px;
              border: 2px solid;
              border-radius: 50%;
              display: grid;
              place-items: center;
              transform-style: preserve-3d;
            }

            .neural-core-sphere {
              position: relative;
              width: 82px;
              height: 82px;
              border-radius: 50%;
              overflow: hidden;
              animation:
                neuralCoreBreathe
                2.2s
                ease-in-out
                infinite;
            }

            .neural-core-highlight {
              position: absolute;
              width: 34%;
              height: 14%;
              top: 20%;
              left: 20%;
              border-radius: 50%;
              background: rgba(255,255,255,0.76);
              filter: blur(5px);
              transform: rotate(-24deg);
            }

            .neural-core-scanline {
              position: absolute;
              left: -20%;
              right: -20%;
              height: 2px;
              top: 10%;
              animation:
                neuralScanline
                1.9s
                linear
                infinite;
            }

            .neural-ring {
              position: absolute;
              left: 50%;
              top: 50%;
              width: 168px;
              height: 70px;
              margin-left: -84px;
              margin-top: -35px;
              border: 1px solid;
              border-radius: 50%;
              opacity: 0.62;
            }

            .neural-ring-a {
              animation:
                neuralRingA
                5.2s
                linear
                infinite;
            }

            .neural-ring-b {
              animation:
                neuralRingB
                6.4s
                linear
                infinite
                reverse;
            }

            .neural-ring-c {
              animation:
                neuralRingC
                7.3s
                linear
                infinite;
            }

            .neural-progress-number {
              position: absolute;
              bottom: 14px;
              font-size: 16px;
              font-weight: 950;
              letter-spacing: 0.08em;
            }

            .neural-core-label {
              position: absolute;
              bottom: -9px;
              color: rgba(228,248,252,0.62);
              font-size: 9px;
              font-weight: 900;
              letter-spacing: 0.16em;
            }

            .neural-trace {
              position: absolute;
              height: 2px;
              opacity: 0.47;
              z-index: 1;
            }

            .trace-a {
              width: 28%;
              left: 8%;
              top: 31%;
            }

            .trace-b {
              width: 28%;
              right: 8%;
              top: 31%;
            }

            .trace-c {
              width: 28%;
              left: 8%;
              bottom: 31%;
            }

            .trace-d {
              width: 28%;
              right: 8%;
              bottom: 31%;
            }

            .neural-pulse {
              position: absolute;
              width: 7px;
              height: 7px;
              border-radius: 50%;
              z-index: 3;
            }

            .pulse-a {
              left: 9%;
              top: calc(31% - 3px);
              animation:
                neuralPulseRight
                2.1s
                linear
                infinite;
            }

            .pulse-b {
              right: 9%;
              top: calc(31% - 3px);
              animation:
                neuralPulseLeft
                2.4s
                linear
                infinite;
            }

            .pulse-c {
              left: 9%;
              bottom: calc(31% - 3px);
              animation:
                neuralPulseRight
                2.7s
                linear
                infinite;
            }

            .neural-v2-status {
              margin-top: 18px;
              padding: 16px 18px;
              border: 1px solid;
              border-radius: 18px;
              background: rgba(3,10,19,0.90);
              backdrop-filter: blur(14px);
              -webkit-backdrop-filter: blur(14px);
            }

            .neural-status-row {
              display: grid;
              grid-template-columns: auto 1fr auto;
              align-items: center;
              gap: 11px;
              font-size: 12px;
              font-weight: 850;
              letter-spacing: 0.025em;
            }

            .neural-status-dot {
              width: 9px;
              height: 9px;
              border-radius: 50%;
              animation:
                neuralStatusDot
                1.1s
                ease-in-out
                infinite;
            }

            .neural-status-track {
              height: 8px;
              margin-top: 13px;
              border-radius: 999px;
              background: rgba(255,255,255,0.07);
              overflow: hidden;
            }

            .neural-status-fill {
              height: 100%;
              border-radius: 999px;
              transition: width 480ms ease;
            }

            .neural-auto-transition {
              width: 180px;
              height: 18px;
              margin: 16px auto 0;
              border-radius: 999px;
              opacity: 0;
              filter: blur(8px);
              transform: scaleX(0.25);
            }

            .neural-auto-transition.is-active {
              animation:
                neuralAutoTransition
                1.35s
                ease-in-out
                forwards;
            }

            .neural-v2-footer {
              margin-top: 16px;
              text-align: center;
              font-size: 9px;
              font-weight: 800;
              letter-spacing: 0.13em;
              text-transform: uppercase;
            }

            @keyframes neuralGridTravel {
              from {
                transform:
                  perspective(850px)
                  rotateX(64deg)
                  translateY(0);
              }
              to {
                transform:
                  perspective(850px)
                  rotateX(64deg)
                  translateY(42px);
              }
            }

            @keyframes neuralBoardReady {
              0%, 100% {
                filter: brightness(1);
              }
              50% {
                filter: brightness(1.17);
              }
            }

            @keyframes moduleScan {
              0% {
                transform: translateX(0);
                opacity: 0;
              }
              25% {
                opacity: 0.52;
              }
              100% {
                transform: translateX(320%);
                opacity: 0;
              }
            }

            @keyframes neuralOuterGlow {
              0%, 100% {
                transform: scale(0.92);
                opacity: 0.48;
              }
              50% {
                transform: scale(1.12);
                opacity: 0.92;
              }
            }

            @keyframes neuralProgressFloat {
              0%, 100% {
                transform: translateY(0);
              }
              50% {
                transform: translateY(-6px);
              }
            }

            @keyframes neuralCoreBreathe {
              0%, 100% {
                transform: scale(0.92);
                filter: brightness(0.92);
              }
              50% {
                transform: scale(1.08);
                filter: brightness(1.26);
              }
            }

            @keyframes neuralScanline {
              from {
                transform: translateY(-6px);
                opacity: 0;
              }
              20% {
                opacity: 0.9;
              }
              to {
                transform: translateY(72px);
                opacity: 0;
              }
            }

            @keyframes neuralRingA {
              from {
                transform:
                  rotateX(66deg)
                  rotateZ(0deg);
              }
              to {
                transform:
                  rotateX(66deg)
                  rotateZ(360deg);
              }
            }

            @keyframes neuralRingB {
              from {
                transform:
                  rotateX(68deg)
                  rotateZ(61deg);
              }
              to {
                transform:
                  rotateX(68deg)
                  rotateZ(421deg);
              }
            }

            @keyframes neuralRingC {
              from {
                transform:
                  rotateX(70deg)
                  rotateZ(121deg);
              }
              to {
                transform:
                  rotateX(70deg)
                  rotateZ(481deg);
              }
            }

            @keyframes neuralPulseRight {
              from {
                transform: translateX(0);
                opacity: 0;
              }
              15% {
                opacity: 1;
              }
              to {
                transform: translateX(320px);
                opacity: 0;
              }
            }

            @keyframes neuralPulseLeft {
              from {
                transform: translateX(0);
                opacity: 0;
              }
              15% {
                opacity: 1;
              }
              to {
                transform: translateX(-320px);
                opacity: 0;
              }
            }

            @keyframes neuralStatusDot {
              0%, 100% {
                transform: scale(0.78);
                opacity: 0.5;
              }
              50% {
                transform: scale(1.25);
                opacity: 1;
              }
            }

            @keyframes neuralAutoTransition {
              0% {
                opacity: 0;
                transform: scaleX(0.25);
              }
              35% {
                opacity: 0.85;
                transform: scaleX(1);
              }
              100% {
                opacity: 0;
                transform: scaleX(1.85);
              }
            }

            @media (max-width: 760px) {
              .neural-v2-shell {
                justify-content: flex-start;
              }

              .neural-v2-header {
                margin-bottom: 14px;
              }

              .neural-v2-board {
                min-height: 515px;
                padding: 15px;
              }

              .neural-system-grid {
                inset: 15px;
                grid-template-columns:
                  repeat(2, minmax(0, 1fr));
                grid-template-rows:
                  94px 1fr 94px;
                gap: 10px;
              }

              .neural-system-module:nth-child(2),
              .neural-system-module:nth-child(5) {
                visibility: visible;
              }

              .neural-core-zone {
                position: absolute;
                left: 50%;
                top: 50%;
                width: 230px;
                height: 230px;
                transform: translate(-50%, -50%);
              }

              .neural-progress-ring {
                width: 196px;
                height: 196px;
              }

              .neural-core-shell {
                width: 124px;
                height: 124px;
              }

              .neural-core-sphere {
                width: 71px;
                height: 71px;
              }

              .neural-ring {
                width: 145px;
                height: 62px;
                margin-left: -72.5px;
                margin-top: -31px;
              }

              .neural-system-module {
                padding: 10px;
              }

              .system-module-code {
                width: 32px;
                height: 32px;
                border-radius: 10px;
              }

              .system-module-copy strong {
                font-size: 10px;
              }

              .system-module-copy span {
                font-size: 8px;
              }

              .trace-a,
              .trace-b,
              .trace-c,
              .trace-d {
                display: none;
              }

              .neural-data-column {
                display: none;
              }
            }

            @media (max-height: 720px) {
              .neural-v2-shell {
                padding-top:
                  max(
                    10px,
                    env(safe-area-inset-top)
                  );
              }

              .neural-v2-header h1 {
                font-size: 27px;
              }

              .neural-v2-board {
                min-height: 430px;
              }
            }

            @media (prefers-reduced-motion: reduce) {
              .neural-background-grid,
              .neural-v2-board,
              .system-module-scan,
              .neural-core-outer-glow,
              .neural-progress-ring,
              .neural-core-sphere,
              .neural-ring,
              .neural-pulse,
              .neural-status-dot,
              .neural-auto-transition {
                animation: none !important;
              }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        background:
          "radial-gradient(circle at 50% 6%, rgba(0,220,255,0.13), transparent 29%), linear-gradient(180deg, #020713 0%, #030914 48%, #010307 100%)",
        color: "#effcff",
        fontFamily:
          'Inter, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <main
        style={{
          width: "min(100%, 620px)",
          minHeight: "100dvh",
          margin: "0 auto",
          padding:
            "max(18px, env(safe-area-inset-top)) 16px max(22px, env(safe-area-inset-bottom))",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            marginBottom: "10px",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: 900,
              letterSpacing: "0.25em",
              color: activeTheme.accent,
              textTransform: "uppercase",
              textShadow: `0 0 14px ${activeTheme.soft}`,
            }}
          >
            AVAAIO™
          </div>

          <div
            aria-label={`Setup step ${step + 1} of ${steps.length}`}
            style={{
              padding: "7px 11px",
              borderRadius: "999px",
              border: "1px solid rgba(150,240,255,0.17)",
              background: "rgba(7,16,28,0.76)",
              color: "rgba(230,251,255,0.76)",
              fontSize: "12px",
              fontWeight: 800,
            }}
          >
            {step + 1} of {steps.length}
          </div>
        </header>

        <section
          aria-label="Ava orbital setup core"
          style={{
            position: "relative",
            height: "clamp(180px, 29dvh, 252px)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            perspective: "900px",
            overflow: "visible",
          }}
        >
          <div
            aria-hidden="true"
            className="avaaio-orbital-field"
            style={{
              position: "absolute",
              width: "clamp(184px, 46vw, 244px)",
              height: "clamp(184px, 46vw, 244px)",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${activeTheme.soft} 0%, rgba(0,0,0,0) 70%)`,
              filter: "blur(19px)",
              opacity: 0.92,
            }}
          />

          <div
            aria-hidden="true"
            className="avaaio-orbital-shell"
            style={{
              position: "relative",
              width: "clamp(150px, 38vw, 204px)",
              height: "clamp(150px, 38vw, 204px)",
              transformStyle: "preserve-3d",
            }}
          >
            <div
              className="avaaio-orbit avaaio-orbit-one"
              style={{
                borderColor: activeTheme.accent,
                boxShadow: `0 0 13px ${activeTheme.soft}, inset 0 0 8px ${activeTheme.soft}`,
              }}
            >
              <span
                className="avaaio-energy-node node-one"
                style={{
                  background: activeTheme.accent,
                  boxShadow: `0 0 10px ${activeTheme.accent}, 0 0 22px ${activeTheme.soft}`,
                }}
              />
            </div>

            <div
              className="avaaio-orbit avaaio-orbit-two"
              style={{
                borderColor: activeTheme.accent,
                boxShadow: `0 0 12px ${activeTheme.soft}, inset 0 0 8px ${activeTheme.soft}`,
              }}
            >
              <span
                className="avaaio-energy-node node-two"
                style={{
                  background: activeTheme.accent,
                  boxShadow: `0 0 10px ${activeTheme.accent}, 0 0 22px ${activeTheme.soft}`,
                }}
              />
            </div>

            <div
              className="avaaio-orbit avaaio-orbit-three"
              style={{
                borderColor: activeTheme.accent,
                boxShadow: `0 0 12px ${activeTheme.soft}, inset 0 0 8px ${activeTheme.soft}`,
              }}
            >
              <span
                className="avaaio-energy-node node-three"
                style={{
                  background: activeTheme.accent,
                  boxShadow: `0 0 10px ${activeTheme.accent}, 0 0 22px ${activeTheme.soft}`,
                }}
              />
            </div>

            <div
              className="avaaio-orbit avaaio-orbit-four"
              style={{
                borderColor: activeTheme.accent,
                boxShadow: `0 0 11px ${activeTheme.soft}`,
              }}
            />

            <div
              className="avaaio-core-halo"
              style={{
                background: `radial-gradient(circle, ${activeTheme.soft}, rgba(0,0,0,0) 70%)`,
              }}
            />

            <div
              className="avaaio-core"
              style={{
                background: `radial-gradient(circle at 35% 28%, #ffffff 0%, #eaffff 8%, ${activeTheme.accent} 23%, ${activeTheme.accent} 43%, #07111d 70%, #010308 100%)`,
                borderColor: activeTheme.accent,
                boxShadow: `0 0 15px ${activeTheme.accent}, 0 0 38px ${activeTheme.soft}, 0 0 75px ${activeTheme.soft}, inset 0 0 21px rgba(255,255,255,0.40)`,
              }}
            >
              <div className="avaaio-core-inner" />
              <div className="avaaio-core-highlight" />
              <div
                className="avaaio-core-scan"
                style={{
                  background: `linear-gradient(90deg, transparent, ${activeTheme.accent}, transparent)`,
                }}
              />
            </div>

            <div
              className="avaaio-data-arc data-arc-one"
              style={{ borderColor: activeTheme.accent }}
            />

            <div
              className="avaaio-data-arc data-arc-two"
              style={{ borderColor: activeTheme.accent }}
            />
          </div>
        </section>

        <section
          style={{
            borderRadius: "26px",
            border: `1px solid ${activeTheme.soft}`,
            background:
              "linear-gradient(180deg, rgba(9,18,32,0.94), rgba(4,9,18,0.97))",
            boxShadow: `0 18px 60px rgba(0,0,0,0.42), 0 0 26px ${activeTheme.soft}`,
            padding: "clamp(18px, 4vw, 26px)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
          }}
        >
          <div
            style={{
              color: activeTheme.accent,
              fontSize: "11px",
              fontWeight: 900,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            {current.eyebrow}
          </div>

          <h1
            style={{
              margin: 0,
              fontSize: "clamp(25px, 7vw, 38px)",
              lineHeight: 1.1,
              letterSpacing: "-0.025em",
              color: "#f5ffff",
            }}
          >
            {current.title}
          </h1>

          <p
            style={{
              margin: "10px 0 20px",
              color: "rgba(225,245,250,0.72)",
              lineHeight: 1.55,
              fontSize: "14px",
            }}
          >
            {current.description}
          </p>

          <form onSubmit={finishSetup}>
            {step === 0 && (
              <label
                style={{
                  display: "block",
                  color: "#dffcff",
                  fontSize: "13px",
                  fontWeight: 800,
                }}
              >
                Display name
                <input
                  type="text"
                  value={displayName}
                  onChange={(event) => {
                    setDisplayName(event.target.value);
                    setMessage("");
                  }}
                  autoComplete="name"
                  enterKeyHint="next"
                  maxLength={80}
                  placeholder="What should Ava call you?"
                  autoFocus
                  style={{
                    width: "100%",
                    minHeight: "54px",
                    marginTop: "8px",
                    padding: "0 15px",
                    borderRadius: "15px",
                    border: `1px solid ${activeTheme.soft}`,
                    background: "rgba(3,10,19,0.94)",
                    color: "#ffffff",
                    fontSize: "16px",
                    outline: "none",
                    boxSizing: "border-box",
                  }}
                />
              </label>
            )}

            {step === 1 && (
              <div
                role="radiogroup"
                aria-label="Choose a theme color"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: "10px",
                }}
              >
                {THEMES.map((theme) => {
                  const selected = theme.name === themeColor;

                  return (
                    <button
                      key={theme.name}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        setThemeColor(theme.name);
                        setTheme(theme.name);
                        setMessage("");
                      }}
                      style={{
                        minHeight: "88px",
                        padding: "10px 6px",
                        borderRadius: "17px",
                        border: selected
                          ? `2px solid ${theme.accent}`
                          : "1px solid rgba(255,255,255,0.10)",
                        background: selected
                          ? `radial-gradient(circle at center, ${theme.soft}, rgba(4,10,18,0.92) 72%)`
                          : "rgba(5,11,20,0.86)",
                        color: "#effcff",
                        cursor: "pointer",
                        boxShadow: selected
                          ? `0 0 18px ${theme.soft}`
                          : "none",
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          display: "block",
                          width: "34px",
                          height: "34px",
                          margin: "0 auto 8px",
                          borderRadius: "50%",
                          background: `radial-gradient(circle at 34% 28%, #ffffff, ${theme.accent} 35%, #02050a 82%)`,
                          border: `1px solid ${theme.accent}`,
                          boxShadow: `0 0 13px ${theme.soft}`,
                        }}
                      />
                      <span
                        style={{
                          display: "block",
                          fontSize: "11px",
                          fontWeight: 900,
                        }}
                      >
                        {theme.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) minmax(0, 0.75fr)",
                  gap: "10px",
                }}
              >
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 800,
                  }}
                >
                  City
                  <input
                    type="text"
                    value={city}
                    onChange={(event) => setCity(event.target.value)}
                    autoComplete="address-level2"
                    placeholder="City"
                    style={{
                      width: "100%",
                      minHeight: "52px",
                      marginTop: "8px",
                      padding: "0 14px",
                      borderRadius: "15px",
                      border: `1px solid ${activeTheme.soft}`,
                      background: "rgba(3,10,19,0.94)",
                      color: "#ffffff",
                      fontSize: "16px",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </label>

                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 800,
                  }}
                >
                  State
                  <input
                    type="text"
                    value={stateName}
                    onChange={(event) => setStateName(event.target.value)}
                    autoComplete="address-level1"
                    placeholder="State"
                    style={{
                      width: "100%",
                      minHeight: "52px",
                      marginTop: "8px",
                      padding: "0 14px",
                      borderRadius: "15px",
                      border: `1px solid ${activeTheme.soft}`,
                      background: "rgba(3,10,19,0.94)",
                      color: "#ffffff",
                      fontSize: "16px",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </label>
              </div>
            )}

            {step === 3 && (
              <div style={{ display: "grid", gap: "13px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 800,
                  }}
                >
                  Age range
                  <select
                    value={ageRange}
                    onChange={(event) => setAgeRange(event.target.value)}
                    style={{
                      width: "100%",
                      minHeight: "52px",
                      marginTop: "8px",
                      padding: "0 14px",
                      borderRadius: "15px",
                      border: `1px solid ${activeTheme.soft}`,
                      background: "rgba(3,10,19,0.94)",
                      color: "#ffffff",
                      fontSize: "16px",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="under-18">
                      Under 18 with parent or guardian
                    </option>
                    <option value="18-24">18–24</option>
                    <option value="25-34">25–34</option>
                    <option value="35-44">35–44</option>
                    <option value="45-54">45–54</option>
                    <option value="55+">55+</option>
                  </select>
                </label>

                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: 800,
                  }}
                >
                  Contact email
                  <span
                    style={{
                      marginLeft: "6px",
                      color: "rgba(225,245,250,0.55)",
                      fontWeight: 600,
                    }}
                  >
                    optional
                  </span>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(event) => setContactEmail(event.target.value)}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    style={{
                      width: "100%",
                      minHeight: "52px",
                      marginTop: "8px",
                      padding: "0 14px",
                      borderRadius: "15px",
                      border: `1px solid ${activeTheme.soft}`,
                      background: "rgba(3,10,19,0.94)",
                      color: "#ffffff",
                      fontSize: "16px",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </label>
              </div>
            )}

            {message && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginTop: "15px",
                  padding: "12px 13px",
                  borderRadius: "14px",
                  border:
                    messageType === "success"
                      ? "1px solid rgba(60,255,190,0.30)"
                      : "1px solid rgba(255,110,130,0.30)",
                  background:
                    messageType === "success"
                      ? "rgba(20,210,145,0.09)"
                      : "rgba(255,70,95,0.09)",
                  color:
                    messageType === "success" ? "#baffea" : "#ffd0d8",
                  fontSize: "13px",
                  lineHeight: 1.5,
                  fontWeight: 700,
                }}
              >
                {message}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: step > 0 ? "0.72fr 1.28fr" : "1fr",
                gap: "10px",
                marginTop: "19px",
              }}
            >
              {step > 0 && (
                <button
                  type="button"
                  onClick={previousStep}
                  disabled={saving}
                  style={{
                    minHeight: "52px",
                    borderRadius: "15px",
                    border: "1px solid rgba(160,235,255,0.18)",
                    background: "rgba(8,16,28,0.94)",
                    color: "#dffcff",
                    fontSize: "15px",
                    fontWeight: 900,
                    cursor: saving ? "not-allowed" : "pointer",
                    opacity: saving ? 0.58 : 1,
                  }}
                >
                  Back
                </button>
              )}

              <button
                type={isLastStep ? "submit" : "button"}
                onClick={isLastStep ? undefined : nextStep}
                disabled={saving || !canContinue}
                style={{
                  minHeight: "52px",
                  borderRadius: "15px",
                  border: "none",
                  background: `linear-gradient(100deg, ${activeTheme.accent}, #d8ffff)`,
                  color: "#031118",
                  fontSize: "15px",
                  fontWeight: 950,
                  cursor:
                    saving || !canContinue ? "not-allowed" : "pointer",
                  opacity: saving || !canContinue ? 0.56 : 1,
                  boxShadow: `0 0 20px ${activeTheme.soft}`,
                }}
              >
                {saving
                  ? "Saving your profile…"
                  : isLastStep
                    ? "Finish & Meet Ava"
                    : "Continue"}
              </button>
            </div>
          </form>
        </section>

        <footer
          style={{
            marginTop: "auto",
            padding: "17px 4px 2px",
            textAlign: "center",
            color: "rgba(215,240,245,0.56)",
            fontSize: "10px",
            lineHeight: 1.55,
          }}
        >
          Ava is powered by AVAAIO™
          <br />
          © 2026 American Globalo, LLC. All rights reserved.
        </footer>
      </main>

      <style>
        {`
          @keyframes avaaioSetupOrb {
            0%, 100% {
              transform: translateY(0) scale(1);
            }
            50% {
              transform: translateY(-5px) scale(1.025);
            }
          }

          @keyframes avaaioSetupGlow {
            0%, 100% {
              transform: scale(0.94);
              opacity: 0.60;
            }
            50% {
              transform: scale(1.08);
              opacity: 0.95;
            }
          }

          .avaaio-orbital-field {
            animation: avaaioOrbitalField 3.2s ease-in-out infinite;
          }

          .avaaio-orbital-shell {
            animation: avaaioOrbitalFloat 4.5s ease-in-out infinite;
          }

          .avaaio-orbit {
            position: absolute;
            inset: 50%;
            width: 100%;
            height: 42%;
            border: 1.5px solid;
            border-radius: 50%;
            transform-origin: center center;
            opacity: 0.86;
          }

          .avaaio-orbit-one {
            transform: translate(-50%, -50%) rotateX(64deg) rotateZ(4deg);
            animation: avaaioOrbitOne 5.2s linear infinite;
          }

          .avaaio-orbit-two {
            transform: translate(-50%, -50%) rotateX(66deg) rotateZ(62deg);
            animation: avaaioOrbitTwo 6.4s linear infinite reverse;
          }

          .avaaio-orbit-three {
            transform: translate(-50%, -50%) rotateX(69deg) rotateZ(119deg);
            animation: avaaioOrbitThree 7.1s linear infinite;
          }

          .avaaio-orbit-four {
            width: 70%;
            height: 70%;
            transform: translate(-50%, -50%) rotateX(72deg) rotateY(68deg);
            opacity: 0.45;
            animation: avaaioOrbitFour 4.8s linear infinite reverse;
          }

          .avaaio-energy-node {
            position: absolute;
            width: 8px;
            height: 8px;
            border-radius: 50%;
          }

          .node-one {
            left: 7%;
            top: 47%;
          }

          .node-two {
            right: 12%;
            top: 21%;
          }

          .node-three {
            left: 46%;
            bottom: -4px;
          }

          .avaaio-core-halo {
            position: absolute;
            width: 56%;
            height: 56%;
            left: 22%;
            top: 22%;
            border-radius: 50%;
            filter: blur(13px);
            animation: avaaioCoreHalo 2.4s ease-in-out infinite;
          }

          .avaaio-core {
            position: absolute;
            width: 43%;
            height: 43%;
            left: 28.5%;
            top: 28.5%;
            border-radius: 50%;
            border: 2px solid;
            overflow: hidden;
            animation: avaaioCoreBreathe 2.8s ease-in-out infinite;
          }

          .avaaio-core-inner {
            position: absolute;
            inset: 12%;
            border-radius: 50%;
            border: 1px solid rgba(255,255,255,0.38);
            box-shadow: inset 0 0 17px rgba(255,255,255,0.16);
          }

          .avaaio-core-highlight {
            position: absolute;
            width: 32%;
            height: 13%;
            top: 20%;
            left: 21%;
            border-radius: 50%;
            background: rgba(255,255,255,0.72);
            filter: blur(5px);
            transform: rotate(-24deg);
          }

          .avaaio-core-scan {
            position: absolute;
            left: -20%;
            right: -20%;
            height: 2px;
            top: 20%;
            opacity: 0.8;
            animation: avaaioCoreScan 2.2s linear infinite;
          }

          .avaaio-data-arc {
            position: absolute;
            width: 31%;
            height: 31%;
            border-style: solid;
            border-width: 2px;
            border-radius: 50%;
            opacity: 0.65;
          }

          .data-arc-one {
            left: 7%;
            top: 7%;
            border-right-color: transparent !important;
            border-bottom-color: transparent !important;
            animation: avaaioDataArc 3.1s linear infinite;
          }

          .data-arc-two {
            right: 7%;
            bottom: 7%;
            border-left-color: transparent !important;
            border-top-color: transparent !important;
            animation: avaaioDataArc 3.1s linear infinite reverse;
          }

          @keyframes avaaioOrbitalField {
            0%, 100% { transform: scale(0.94); opacity: 0.58; }
            50% { transform: scale(1.09); opacity: 0.96; }
          }

          @keyframes avaaioOrbitalFloat {
            0%, 100% { transform: translateY(0) rotateZ(-2deg); }
            50% { transform: translateY(-6px) rotateZ(2deg); }
          }

          @keyframes avaaioOrbitOne {
            from { transform: translate(-50%, -50%) rotateX(64deg) rotateZ(4deg); }
            to { transform: translate(-50%, -50%) rotateX(64deg) rotateZ(364deg); }
          }

          @keyframes avaaioOrbitTwo {
            from { transform: translate(-50%, -50%) rotateX(66deg) rotateZ(62deg); }
            to { transform: translate(-50%, -50%) rotateX(66deg) rotateZ(422deg); }
          }

          @keyframes avaaioOrbitThree {
            from { transform: translate(-50%, -50%) rotateX(69deg) rotateZ(119deg); }
            to { transform: translate(-50%, -50%) rotateX(69deg) rotateZ(479deg); }
          }

          @keyframes avaaioOrbitFour {
            from { transform: translate(-50%, -50%) rotateX(72deg) rotateY(68deg) rotateZ(0deg); }
            to { transform: translate(-50%, -50%) rotateX(72deg) rotateY(68deg) rotateZ(360deg); }
          }

          @keyframes avaaioCoreHalo {
            0%, 100% { transform: scale(0.88); opacity: 0.58; }
            50% { transform: scale(1.16); opacity: 1; }
          }

          @keyframes avaaioCoreBreathe {
            0%, 100% { transform: scale(0.96); filter: brightness(0.92); }
            50% { transform: scale(1.05); filter: brightness(1.18); }
          }

          @keyframes avaaioCoreScan {
            from { transform: translateY(-8px); opacity: 0; }
            20% { opacity: 0.9; }
            to { transform: translateY(58px); opacity: 0; }
          }

          @keyframes avaaioDataArc {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @media (max-height: 680px) {
            main {
              padding-top: max(10px, env(safe-area-inset-top)) !important;
            }
          }

          @media (prefers-reduced-motion: reduce) {
            * {
              animation-duration: 0.001ms !important;
              animation-iteration-count: 1 !important;
            }
          }
        `}
      </style>
    </div>
  );
}
